import type { Schemas } from "@norn/sdk"
import { useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { CornerDownLeft, FileText, Home, Map as MapIcon, Search, Shapes, Wrench } from "lucide-react"
import { Dialog } from "radix-ui"
import { type ReactNode, useEffect, useMemo, useState, useSyncExternalStore } from "react"
import type { Scope } from "@/lib/connection"
import { useLocale, useT } from "@/lib/i18n"
import { useCell } from "@/lib/session"
import { cn } from "@/lib/utils"
import { useHistorySpace, useOntology, useRecent } from "./hooks"
import { recordTitle } from "./ontology"

// One palette per tab; any control can open it.
let open = false
const listeners = new Set<() => void>()
function setOpen(next: boolean) {
  open = next
  for (const l of listeners) l()
}
export function openCommandPalette() {
  setOpen(true)
}

interface Item {
  readonly id: string
  readonly group: string
  readonly label: string
  readonly hint?: string
  readonly icon: ReactNode
  readonly go: () => void
}

/** Records already read in this tab (lists, previews, details), searchable by title or key. */
function cachedRecords(client: ReturnType<typeof useQueryClient>, baseUrl: string, scope: Scope): Schemas["ObjectResponse"][] {
  const out = new Map<string, Schemas["ObjectResponse"]>()
  for (const q of client.getQueryCache().getAll()) {
    // Only reads of this cell and scope (keys are [kind, baseUrl, orgId, environmentId, ...]).
    if (q.queryKey[1] !== baseUrl || q.queryKey[2] !== scope.orgId || q.queryKey[3] !== scope.environmentId) continue
    const key = q.queryKey[0]
    const data = q.state.data as unknown
    if (!data || typeof data !== "object") continue
    if (key === "object" && "key" in data && "objectType" in data) {
      const o = data as Schemas["ObjectResponse"]
      out.set(`${o.objectType}/${o.key}`, o)
    } else if (key === "objects" && "items" in data) {
      for (const o of (data as Schemas["ObjectList"]).items) out.set(`${o.objectType}/${o.key}`, o)
    } else if (key === "ws-pages" && "pages" in data) {
      for (const page of (data as { pages: Schemas["ObjectList"][] }).pages) for (const o of page.items) out.set(`${o.objectType}/${o.key}`, o)
    }
  }
  return [...out.values()]
}

export function CommandPalette({ scope }: { scope: Scope }) {
  const isOpen = useSyncExternalStore(
    (l) => {
      listeners.add(l)
      return () => listeners.delete(l)
    },
    () => open,
    () => false,
  )
  const t = useT()
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault()
        setOpen(!open)
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [])
  return (
    <Dialog.Root open={isOpen} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed top-[14vh] left-1/2 z-50 flex max-h-[70vh] w-[min(36rem,calc(100vw-2rem))] -translate-x-1/2 flex-col overflow-hidden rounded-2xl border border-border bg-popover shadow-2xl outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[0.98]"
        >
          <Dialog.Title className="sr-only">{t("ws.search.title")}</Dialog.Title>
          {isOpen ? <PaletteBody scope={scope} close={() => setOpen(false)} /> : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function PaletteBody({ scope, close }: { scope: Scope; close: () => void }) {
  const t = useT()
  const navigate = useNavigate()
  const client = useQueryClient()
  const { connection } = useCell()
  const { preference } = useLocale()
  const { ontology } = useOntology(scope)
  const recent = useRecent(useHistorySpace(scope))
  const [query, setQuery] = useState("")
  const [active, setActive] = useState(0)
  const q = query.trim().toLowerCase()

  const items = useMemo(() => {
    const go = (fn: () => void) => () => {
      close()
      fn()
    }
    const list: Item[] = []
    const record = (objectType: string, key: string) => go(() => void navigate({ to: "/$orgId/$environmentId/records/$objectType/$key", params: { ...scope, objectType, key } }))
    const nav: Item[] = [
      { id: "nav:home", group: t("ws.search.go"), label: t("ws.nav.home"), icon: <Home />, go: go(() => void navigate({ to: "/$orgId/$environmentId", params: scope })) },
      { id: "nav:areas", group: t("ws.search.go"), label: t("ws.nav.areas"), icon: <MapIcon />, go: go(() => void navigate({ to: "/$orgId/$environmentId/areas", params: scope })) },
      { id: "nav:builder", group: t("ws.search.go"), label: t("ws.mode.builder"), icon: <Wrench />, go: go(() => void navigate({ to: "/$orgId/$environmentId/builder", params: scope })) },
    ]
    const match = (s: string) => !q || s.toLowerCase().includes(q)
    list.push(...nav.filter((i) => match(i.label)))
    if (!q) for (const r of recent.slice(0, 5)) list.push({ id: `recent:${r.objectType}/${r.key}`, group: t("ws.home.recent"), label: r.title, hint: ontology?.types.get(r.objectType)?.label, icon: <FileText />, go: record(r.objectType, r.key) })
    if (ontology) {
      for (const area of ontology.areas)
        if (match(area.label)) list.push({ id: `area:${area.id}`, group: t("ws.nav.businessAreas"), label: area.label, icon: <MapIcon />, go: go(() => void navigate({ to: "/$orgId/$environmentId/areas/$areaId", params: { ...scope, areaId: area.id } })) })
      const types = ontology.areas.flatMap((a) => a.types.map((n) => ({ type: ontology.types.get(n), area: a })))
      for (const { type, area } of types)
        if (type && q && (match(type.pluralLabel) || match(type.name)))
          list.push({ id: `type:${type.name}`, group: t("ws.search.collections"), label: type.pluralLabel, hint: area.label, icon: <Shapes />, go: go(() => void navigate({ to: "/$orgId/$environmentId/records/$objectType", params: { ...scope, objectType: type.name } })) })
    }
    if (q.length >= 2) {
      const records = cachedRecords(client, connection.baseUrl, scope)
      let n = 0
      const prefixes = new Map<string, string>()
      for (const o of records) {
        const type = ontology?.types.get(o.objectType)
        const title = recordTitle(type, o.values as Record<string, unknown>, o.masked as Record<string, unknown> | undefined, preference.locale).title
        const prefix = o.key.replace(/[0-9]+$/, "")
        if (prefix && prefix !== o.key) prefixes.set(prefix.toLowerCase(), o.objectType)
        if (n < 12 && (o.key.toLowerCase().includes(q) || title?.toLowerCase().includes(q))) {
          n += 1
          list.push({ id: `rec:${o.objectType}/${o.key}`, group: t("ws.search.records"), label: title ?? o.key, hint: `${type?.label ?? o.objectType} · ${o.key}`, icon: <FileText />, go: record(o.objectType, o.key) })
        }
      }
      // An exact key not read yet: offer the type whose keys share its prefix; the cell still authorizes the read.
      const exact = query.trim()
      const owner = prefixes.get(exact.replace(/[0-9]+$/, "").toLowerCase())
      if (owner && !list.some((i) => i.id === `rec:${owner}/${exact}`))
        list.push({ id: `open:${owner}/${exact}`, group: t("ws.search.records"), label: t("ws.search.openKey", { key: exact }), hint: ontology?.types.get(owner)?.label, icon: <CornerDownLeft />, go: record(owner, exact) })
    }
    return list
  }, [q, query, ontology, recent, client, connection.baseUrl, scope, navigate, close, t, preference.locale])

  const groups = [...new Set(items.map((i) => i.group))]
  return (
    <>
      <div className="flex items-center gap-3 border-b border-border px-4">
        <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        <input
          // biome-ignore lint/a11y/noAutofocus: the palette exists to be typed into.
          autoFocus
          role="combobox"
          aria-expanded="true"
          aria-controls="ws-palette-list"
          aria-activedescendant={items[active] ? `ws-palette-${active}` : undefined}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setActive(0)
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault()
              setActive((a) => Math.min(items.length - 1, a + 1))
            } else if (e.key === "ArrowUp") {
              e.preventDefault()
              setActive((a) => Math.max(0, a - 1))
            } else if (e.key === "Enter") {
              e.preventDefault()
              items[active]?.go()
            }
          }}
          placeholder={t("ws.search.placeholder")}
          aria-label={t("ws.search.title")}
          className="h-12 flex-1 bg-transparent text-[15px] outline-none placeholder:text-muted-foreground"
        />
        <kbd className="font-sans text-2xs text-subtle-foreground">esc</kbd>
      </div>
      <div id="ws-palette-list" role="listbox" aria-label={t("ws.search.title")} className="min-h-0 flex-1 overflow-y-auto p-2">
        {items.length === 0 ? <p className="px-3 py-6 text-center text-sm text-muted-foreground">{ontology ? t("ws.search.nothing") : t("states.loading")}</p> : null}
        {groups.map((group) => (
          <div key={group} role="presentation" className="pb-1">
            <p className="px-3 pt-2 pb-1 text-[11px] font-medium text-subtle-foreground">{group}</p>
            {items.map((item, index) =>
              item.group === group ? (
                <div
                  key={item.id}
                  id={`ws-palette-${index}`}
                  role="option"
                  tabIndex={-1}
                  aria-selected={index === active}
                  onMouseMove={() => setActive(index)}
                  onClick={item.go}
                  onKeyDown={() => undefined}
                  className={cn("flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2 text-sm [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-muted-foreground", index === active && "bg-accent")}
                >
                  {item.icon}
                  <span className="min-w-0 flex-1 truncate" dir="auto">
                    {item.label}
                  </span>
                  {item.hint ? <span className="truncate text-xs text-subtle-foreground">{item.hint}</span> : null}
                </div>
              ) : null,
            )}
          </div>
        ))}
      </div>
    </>
  )
}
